/**
 * The wordmark's slice, cut once. E47 § 3.5.
 *
 * The artwork's rest state is OUT -- `#wm-cake-slice` is drawn beside the cake,
 * not in it -- so this module only ever animates TO that state. A visitor with
 * no JavaScript, and a visitor who asked for less motion, see the mark the
 * client knows, and there is no third position to design.
 *
 * ONCE PER PAGE LOAD, AND NEVER PER SWUP NAVIGATION. `header` is a swup
 * container, so the bar is replaced on every visit: in app.js's MODULES
 * registry this would replay on all eighteen pages, which is a mark that keeps
 * cutting itself rather than an entrance. Called beside initAnalytics()
 * instead, outside the registry, for the same reason that one is.
 */

const CUTTING = 'is-cutting'
const HELD = 'is-brand-held'

/* The floor under a visit that never happens -- blocked, aborted, or a link
   Swup hands to the browser and the browser then declines. src/js/nav.js uses
   the same number for the same reason. */
const HELD_TIMEOUT = 2000

/**
 * HOLD THE SLICE SEATED THROUGH THE CLICK THAT LEAVES THE PAGE.
 *
 * `header` is a swup container, so the bar under the pointer is destroyed and
 * rebuilt. Chromium does not re-evaluate `:hover` on freshly inserted markup
 * until the next pointer event, and a hand resting on the mouse produces none
 * -- so the arriving slice starts OUT and snaps back in at the first twitch.
 * Ben saw exactly that, and it is the fault nav.js already pins its line open
 * for.
 *
 * The class goes on <html>, which the swap does not replace. It comes off as
 * soon as the pointer is somewhere else, at which point real pointer events
 * have happened and `:hover` is telling the truth again.
 */
function holdSeat (root) {
  const release = () => root.classList.remove(HELD)

  document.addEventListener('click', (event) => {
    const brand = event.target.closest && event.target.closest('.site-brand')
    if (!brand) return
    /* A modified click leaves this page where it is, so there is no swap to
       hold anything through -- nav.js makes the same three exclusions. */
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (brand.target === '_blank') return

    root.classList.add(HELD)
    setTimeout(release, HELD_TIMEOUT)
  })

  /* Not `pointerleave` on the brand: the element that would fire it is the one
     the swap throws away. The question is only ever "is the pointer still on a
     wordmark", and the arriving one answers it. */
  document.addEventListener('pointermove', (event) => {
    if (!root.classList.contains(HELD)) return
    if (event.target.closest && event.target.closest('.site-brand')) return
    release()
  }, { passive: true })
}

export function initWordmark () {
  const root = document.documentElement
  const brand = document.querySelector('.site-brand')
  const slice = brand && brand.querySelector('.site-brand__slice')
  if (!slice) return

  /* NOT redundant with the media query in components/_site-nav.scss. Under
     `reduce` the animation never runs, so `animationend` never fires, so the
     class would sit on the element for the life of the page -- visible to
     anything that reads the DOM, this site's gates included. */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  holdSeat(root)

  brand.classList.add(CUTTING)
  /* The animation carries --delay-brand-cut and `both`, so the slice sits
     seated for a second before it travels: at zero it had already happened by
     the time the page stopped arriving. animationend fires after the travel,
     not after the wait. */
  slice.addEventListener('animationend', () => brand.classList.remove(CUTTING), { once: true })
}
